/**
 * Membership V2 join modals: wizard steps, plan copy, Stripe Payment Element.
 * Open/close is also handled by MembershipJoinModalLite in main.js.
 */

import {
    startMembershipCheckout,
    unmountPaymentElement,
    confirmMembershipPayment,
    paymentIntentIdFromClientSecret,
    reportMembershipPaymentStatus,
    getStripeReturnStatus,
    clearStripeReturnParams,
    membershipCampaign,
    clearMembershipCampaign,
    validateMembershipPromo,
    formatMembershipMoney,
    formatMembershipDate,
} from './membership-stripe.js';

let stripeSession = emptyStripeSession();

function emptyStripeSession() {
    return {
        plan: '',
        email: '',
        stripe: null,
        elements: null,
        paymentElement: null,
        clientSecret: '',
        subscriptionId: '',
        customerId: '',
        tier: '',
        name: '',
        billing: '',
        abandonReported: false,
        promoCode: '',
        channelHint: '',
        channel: '',
        amountDue: 0,
        discountLabel: '',
        nextChargeAt: 0,
    };
}

let storedPromoPromise = null;
let promoGeneration = 0;

function reportCheckoutAbandoned(wizard) {
    if (!wizard || wizard.classList.contains('is-confirm') || stripeSession.abandonReported) {
        return;
    }
    const step = wizard.querySelector('[data-membership-join-step="2"]');
    if (!step || step.hasAttribute('hidden')) {
        return;
    }
    const intentId = paymentIntentIdFromClientSecret(stripeSession.clientSecret);
    if (!intentId) {
        return;
    }
    stripeSession.abandonReported = true;
    reportMembershipPaymentStatus(intentId, { kind: 'user_cancelled' });
}

function closeOffCanvasMenu() {
    const offCanvas = document.querySelector('.off-canvas--menu');
    if (!offCanvas?.classList.contains('off-canvas--visible')) {
        return;
    }

    offCanvas.classList.remove('off-canvas--visible');
    document.body.classList.remove('off-canvas-menu-open', 'off-canvas-opened-by-search');
}

function setOpenState(modal, isOpen) {
    modal.classList.toggle('is-open', isOpen);
    if (isOpen) {
        modal.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        closeOffCanvasMenu();
    } else {
        modal.setAttribute('hidden', '');
        if (!document.querySelector('.membership-join.is-open, [data-apply-join-modal].is-open')) {
            document.body.style.overflow = '';
        }
    }
}

function applyPlan(wizard, planKey) {
    const panels = wizard.querySelectorAll('[data-membership-join-plan]');
    let active = null;

    panels.forEach((panel) => {
        const isActive = panel.getAttribute('data-membership-join-plan') === planKey;
        panel.toggleAttribute('hidden', !isActive);
        if (isActive) {
            active = panel;
        }
    });

    if (!active) {
        return;
    }

    const title = wizard.querySelector('[data-membership-join-step1-title]');
    const lead = wizard.querySelector('[data-membership-join-step1-lead]');
    const variant = wizard.querySelector('[data-membership-join-variant]');
    const total = wizard.querySelector('[data-membership-join-total]');
    const totalNote = wizard.querySelector('[data-membership-join-total-note]');
    const legal = wizard.querySelector('[data-membership-join-legal]');

    if (title) {
        title.textContent = active.getAttribute('data-step1-title') || title.textContent;
    }
    if (lead) {
        lead.textContent = active.getAttribute('data-step1-lead') || lead.textContent;
    }
    const yearly = wizard.dataset.billing === 'yearly';
    const pick = (name) => {
        const yearlyValue = active.getAttribute(`data-${name}-yearly`);
        const monthlyValue = active.getAttribute(`data-${name}`);
        if (yearly && yearlyValue) {
            return yearlyValue;
        }
        return monthlyValue;
    };

    if (variant) {
        variant.value = active.getAttribute('data-variant') || 'membership-core';
    }
    if (total) {
        total.textContent = pick('total') || total.textContent;
    }
    if (totalNote) {
        totalNote.textContent = pick('total-note') || totalNote.textContent;
    }
    if (legal) {
        const template = pick('legal') || '';
        const amount = pick('amount') || '';
        legal.textContent = template.split('{amount}').join(amount);
    }

    const cancel = active.querySelector('[data-membership-join-cancel]');
    if (cancel) {
        const nextCancel = yearly
            ? active.getAttribute('data-cancel-yearly')
            : active.getAttribute('data-cancel-monthly');
        if (nextCancel) {
            cancel.textContent = nextCancel;
        }
    }

    wizard.dataset.plan = planKey;
}

function applyStepCount(wizard, total) {
    if (total === 2 || total === 3) {
        wizard.dataset.stepCount = String(total);
    }
    return wizard.dataset.stepCount === '3' ? 3 : 2;
}

function setWizardStep(wizard, step, total) {
    const count = applyStepCount(wizard, total);
    const nextStep = step === 2 ? 2 : 1;
    wizard.querySelectorAll('[data-membership-join-step]').forEach((panel) => {
        panel.toggleAttribute('hidden', Number(panel.getAttribute('data-membership-join-step')) !== nextStep);
    });

    wizard.classList.toggle('is-checkout', nextStep === 2);

    const label = wizard.querySelector('[data-membership-join-step-label]');
    if (label) {
        label.textContent = `Step ${nextStep} of ${count}`;
    }

    wizard.querySelectorAll('[data-membership-join-seg]').forEach((seg) => {
        const index = Number(seg.getAttribute('data-membership-join-seg'));
        if (index > count) {
            seg.hidden = true;
            seg.classList.remove('is-on');
            return;
        }
        seg.hidden = false;
        seg.classList.toggle('is-on', index <= nextStep);
    });

    if (nextStep === 2) {
        ensureStoredPromo(wizard);
        const name = wizard.querySelector('[data-membership-join-name]');
        const billed = wizard.querySelector('[data-membership-join-billed-name]');
        if (name && billed && !billed.value) {
            billed.value = name.value;
        }
        const emailField = wizard.querySelector('[data-membership-join-billed-email]');
        if (wizard.classList.contains('is-payment-direct') && emailField && !emailField.value) {
            emailField.focus();
        } else {
            billed?.focus();
        }
    } else {
        invalidateCheckout();
        wizard.querySelector('[data-membership-join-name]')?.focus();
    }
}

function setConfirmState(wizard, isConfirm) {
    const confirm = wizard.querySelector('[data-membership-join-confirm]');
    const formSteps = wizard.querySelectorAll('[data-membership-join-step], [data-membership-join-progress]');
    confirm?.toggleAttribute('hidden', !isConfirm);
    formSteps.forEach((node) => {
        node.toggleAttribute('hidden', isConfirm);
    });
    wizard.classList.toggle('is-confirm', Boolean(isConfirm));
    wizard.classList.toggle('is-checkout', !isConfirm && wizard.querySelector('[data-membership-join-step="2"]:not([hidden])'));
}

function resetWizard(wizard) {
    const form = wizard.querySelector('#membership-join-wizard-form');
    form?.reset();
    hideFormMessage(form);
    storedPromoPromise = null;
    resetPromo(wizard);
    setQuote(wizard, 0);
    setConfirmState(wizard, false);
    wizard.dataset.billing = 'monthly';
    setPaymentDirect(wizard, false);
    setWizardStep(wizard, 1, 2);
}

function setQuote(wizard, index) {
    const slides = [...wizard.querySelectorAll('.membership-join__quote-slide')];
    if (!slides.length) {
        return;
    }

    const next = Math.max(0, Math.min(index, slides.length - 1));
    slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === next);
    });
    wizard.querySelectorAll('[data-membership-join-quote]').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === next);
    });
}

function markFieldValidity(field) {
    if (!field) {
        return true;
    }
    if (!field.checkValidity()) {
        field.classList.add('invalid');
        field.reportValidity();
        return false;
    }
    field.classList.remove('invalid');
    return true;
}

function validateStepOne(wizard) {
    const name = wizard.querySelector('[data-membership-join-name]');
    const email = wizard.querySelector('[data-membership-join-email]');
    return [name, email].every(markFieldValidity);
}

function validateCheckoutFields(wizard) {
    const fields = [
        wizard.querySelector('[data-membership-join-billed-name]'),
        wizard.querySelector('#membership-join-postal'),
    ];
    if (wizard.classList.contains('is-payment-direct')) {
        fields.unshift(wizard.querySelector('[data-membership-join-billed-email]'));
        fields.splice(2, 0, wizard.querySelector('#membership-join-country'));
    }
    return fields.every(markFieldValidity);
}

function wizardIdentity(wizard) {
    const billed = wizard.querySelector('[data-membership-join-billed-name]')?.value?.trim() || '';
    const stepName = wizard.querySelector('[data-membership-join-name]')?.value?.trim() || '';
    const onCheckout = wizard.classList.contains('is-checkout') || wizard.classList.contains('is-payment-direct');
    const name = onCheckout ? billed : (stepName || billed);
    const stepEmail = wizard.querySelector('[data-membership-join-email]')?.value?.trim() || '';
    const billedEmail = wizard.querySelector('[data-membership-join-billed-email]')?.value?.trim() || '';
    const email = wizard.classList.contains('is-payment-direct') ? billedEmail : (stepEmail || billedEmail);
    return {
        name,
        email,
        country: String(wizard.querySelector('#membership-join-country')?.value || '').trim().toUpperCase(),
        postalCode: wizard.querySelector('#membership-join-postal')?.value || '',
        plan: wizard.dataset.plan || 'core',
        billing: wizard.dataset.billing === 'yearly' ? 'yearly' : 'monthly',
        newsletterOptIn: Boolean(wizard.querySelector('[data-membership-join-newsletter]')?.checked),
        promoCode: wizard.dataset.promoCode || '',
        channel: membershipCampaign().channel,
    };
}

let checkoutSeq = 0;

async function prepareCheckout(wizard) {
    await ensureStoredPromo(wizard);
    const identity = wizardIdentity(wizard);
    if (
        stripeSession.paymentElement
        && stripeSession.clientSecret
        && stripeSession.plan === identity.plan
        && stripeSession.email === identity.email
        && stripeSession.name === identity.name
        && stripeSession.billing === identity.billing
        && stripeSession.country === identity.country
        && stripeSession.newsletterOptIn === identity.newsletterOptIn
        && stripeSession.promoCode === identity.promoCode
        && stripeSession.channelHint === identity.channel
    ) {
        return stripeSession;
    }

    const seq = ++checkoutSeq;
    teardownStripe();
    try {
        const session = await startMembershipCheckout(identity);
        if (seq !== checkoutSeq) {
            unmountPaymentElement(session);
            return stripeSession;
        }
        stripeSession = session;
        syncDiscountSummary(wizard, stripeSession);
        return stripeSession;
    } catch (error) {
        if (seq === checkoutSeq) {
            teardownStripe();
        }
        throw error;
    }
}

function teardownStripe() {
    unmountPaymentElement(stripeSession);
    stripeSession = emptyStripeSession();
}

function invalidateCheckout() {
    checkoutSeq += 1;
    teardownStripe();
}

function setButtonLoading(button, isLoading) {
    const text = button?.querySelector('.submit-text');
    const loading = button?.querySelector('.submit-loading');
    if (button) {
        button.disabled = isLoading;
    }
    if (text) {
        text.style.display = isLoading ? 'none' : 'inline';
    }
    if (loading) {
        loading.style.display = isLoading ? 'inline' : 'none';
    }
}

function renderFormMessage(messageDiv, message) {
    const text = String(message || '');
    const email = 'hello@psystandard.com';
    const index = text.indexOf(email);
    messageDiv.replaceChildren();
    if (index === -1) {
        messageDiv.textContent = text;
        return;
    }
    const link = document.createElement('a');
    link.href = `mailto:${email}`;
    link.textContent = email;
    messageDiv.append(
        document.createTextNode(text.slice(0, index)),
        link,
        document.createTextNode(text.slice(index + email.length))
    );
}

function showFormMessage(form, message, isError) {
    const messageDiv = form.querySelector('.form-message');
    if (!messageDiv) {
        return;
    }
    messageDiv.className = isError
        ? 'form-message membership-join__form-message alert alert--error'
        : 'form-message membership-join__form-message alert alert--success';
    renderFormMessage(messageDiv, message);
    messageDiv.style.display = 'block';
}

function hideFormMessage(form) {
    const messageDiv = form?.querySelector('.form-message');
    if (messageDiv) {
        messageDiv.style.display = 'none';
        messageDiv.textContent = '';
    }
}

function hidePromoError(wizard) {
    const error = wizard.querySelector('[data-membership-join-promo-error]');
    if (!error) {
        return;
    }
    error.hidden = true;
    error.textContent = '';
}

function showPromoError(wizard, message) {
    const error = wizard.querySelector('[data-membership-join-promo-error]');
    if (!error) {
        return;
    }
    error.hidden = false;
    error.textContent = message;
}

function openPromoPanel(wizard, focus = true) {
    const panel = wizard.querySelector('[data-membership-join-promo-panel]');
    const toggle = wizard.querySelector('[data-membership-join-promo-toggle]');
    if (panel) {
        panel.hidden = false;
    }
    if (toggle) {
        toggle.hidden = true;
    }
    if (focus) {
        wizard.querySelector('[data-membership-join-promo-input]')?.focus();
    }
}

function resetPromo(wizard) {
    promoGeneration += 1;
    delete wizard.dataset.promoCode;
    delete wizard.dataset.promoLabel;
    const toggle = wizard.querySelector('[data-membership-join-promo-toggle]');
    const panel = wizard.querySelector('[data-membership-join-promo-panel]');
    const entry = wizard.querySelector('[data-membership-join-promo-entry]');
    const applied = wizard.querySelector('[data-membership-join-promo-applied]');
    const input = wizard.querySelector('[data-membership-join-promo-input]');
    if (toggle) {
        toggle.hidden = false;
    }
    if (panel) {
        panel.hidden = true;
    }
    if (entry) {
        entry.hidden = false;
    }
    if (applied) {
        applied.hidden = true;
    }
    if (input) {
        input.value = '';
    }
    hidePromoError(wizard);
    delete wizard.dataset.promoChecked;
}

function setPromoApplied(wizard, result) {
    wizard.dataset.promoCode = result.code || '';
    wizard.dataset.promoLabel = result.label || '';
    wizard.dataset.promoChecked = '1';
    const entry = wizard.querySelector('[data-membership-join-promo-entry]');
    const applied = wizard.querySelector('[data-membership-join-promo-applied]');
    const label = wizard.querySelector('[data-membership-join-promo-label]');
    openPromoPanel(wizard, false);
    if (entry) {
        entry.hidden = true;
    }
    if (applied) {
        applied.hidden = false;
    }
    if (label) {
        label.textContent = [result.code, result.label].filter(Boolean).join(' · ');
    }
    hidePromoError(wizard);
}

function activePlanNode(wizard) {
    const planKey = wizard.dataset.plan || 'core';
    return wizard.querySelector(`[data-membership-join-plan="${planKey}"]`);
}

function syncDiscountSummary(wizard, session) {
    const total = wizard.querySelector('[data-membership-join-total]');
    const note = wizard.querySelector('[data-membership-join-total-note]');
    const legal = wizard.querySelector('[data-membership-join-legal]');
    const active = activePlanNode(wizard);
    if (!active) {
        return;
    }
    const yearly = wizard.dataset.billing === 'yearly';
    const listAmount = (yearly ? active.getAttribute('data-amount-yearly') : active.getAttribute('data-amount')) || '';
    const listTotal = (yearly ? active.getAttribute('data-total-yearly') : active.getAttribute('data-total')) || '';
    const listNote = (yearly ? active.getAttribute('data-total-note-yearly') : active.getAttribute('data-total-note')) || '';
    const template = (yearly ? active.getAttribute('data-legal-yearly') : active.getAttribute('data-legal')) || '';
    const discounted = Boolean(session?.discountLabel) && Number(session?.amountDue) > 0;
    const amountText = discounted ? formatMembershipMoney(session.amountDue, 'eur') : listAmount;
    if (total) {
        total.textContent = discounted ? amountText : listTotal;
    }
    if (note) {
        if (discounted && session.channel === 'partner' && !yearly) {
            note.textContent = `${session.discountLabel} for two more months, then ${listTotal}`;
        } else if (discounted && session.nextChargeAt) {
            note.textContent = `Then ${listTotal} from ${formatMembershipDate(session.nextChargeAt)}`;
        } else {
            note.textContent = listNote;
        }
    }
    if (legal) {
        legal.textContent = template.split('{amount}').join(amountText);
    }
}

async function applyStoredPromo(wizard) {
    const generation = promoGeneration;
    const campaign = membershipCampaign();
    if (!campaign.code) {
        return;
    }
    openPromoPanel(wizard, false);
    const input = wizard.querySelector('[data-membership-join-promo-input]');
    if (input && !input.value) {
        input.value = campaign.code;
    }
    try {
        const result = await validateMembershipPromo({
            code: campaign.code,
            plan: wizard.dataset.plan || 'core',
            billing: wizard.dataset.billing === 'yearly' ? 'yearly' : 'monthly',
            channel: campaign.channel,
        });
        if (generation !== promoGeneration) {
            return;
        }
        setPromoApplied(wizard, result);
        syncDiscountSummary(wizard, {
            amountDue: result.amount_due,
            nextChargeAt: result.next_charge_at,
            discountLabel: result.label,
            channel: result.channel,
        });
    } catch (error) {
        showPromoError(wizard, error.message || 'This code is not valid.');
    }
}

function ensureStoredPromo(wizard) {
    if (wizard.dataset.promoCode || wizard.dataset.promoChecked === '1') {
        return storedPromoPromise || Promise.resolve();
    }
    wizard.dataset.promoChecked = '1';
    storedPromoPromise = applyStoredPromo(wizard);
    return storedPromoPromise;
}

async function applyPromoFromInput(wizard) {
    const input = wizard.querySelector('[data-membership-join-promo-input]');
    const code = (input?.value || '').trim();
    hidePromoError(wizard);
    if (!code) {
        showPromoError(wizard, 'Enter a discount code.');
        return;
    }
    promoGeneration += 1;
    const button = wizard.querySelector('[data-membership-join-promo-apply]');
    if (button) {
        button.disabled = true;
    }
    try {
        const result = await validateMembershipPromo({
            code,
            plan: wizard.dataset.plan || 'core',
            billing: wizard.dataset.billing === 'yearly' ? 'yearly' : 'monthly',
            channel: membershipCampaign().channel,
        });
        setPromoApplied(wizard, result);
        syncDiscountSummary(wizard, {
            amountDue: result.amount_due,
            nextChargeAt: result.next_charge_at,
            discountLabel: result.label,
            channel: result.channel,
        });
        if (identityReady(wizardIdentity(wizard))) {
            const form = wizard.querySelector('#membership-join-wizard-form');
            hideFormMessage(form);
            await prepareCheckout(wizard);
        }
    } catch (error) {
        showPromoError(wizard, error.message || 'This code is not valid.');
    } finally {
        if (button) {
            button.disabled = false;
        }
    }
}

async function removePromo(wizard) {
    promoGeneration += 1;
    delete wizard.dataset.promoCode;
    delete wizard.dataset.promoLabel;
    wizard.dataset.promoChecked = '1';
    const entry = wizard.querySelector('[data-membership-join-promo-entry]');
    const applied = wizard.querySelector('[data-membership-join-promo-applied]');
    const input = wizard.querySelector('[data-membership-join-promo-input]');
    if (entry) {
        entry.hidden = false;
    }
    if (applied) {
        applied.hidden = true;
    }
    if (input) {
        input.value = '';
    }
    hidePromoError(wizard);
    syncDiscountSummary(wizard, null);
    if (!identityReady(wizardIdentity(wizard))) {
        return;
    }
    const form = wizard.querySelector('#membership-join-wizard-form');
    hideFormMessage(form);
    try {
        await prepareCheckout(wizard);
    } catch (error) {
        showFormMessage(form, error.message || 'Could not start checkout. Please try again.', true);
    }
}

async function goToCheckout(wizard) {
    if (!validateStepOne(wizard)) {
        return;
    }

    const form = wizard.querySelector('#membership-join-wizard-form');
    const continueBtn = wizard.querySelector('[data-membership-join-continue]');
    hideFormMessage(form);
    setWizardStep(wizard, 2);
    if (!identityReady(wizardIdentity(wizard))) {
        return;
    }
    setButtonLoading(continueBtn, true);
    try {
        await prepareCheckout(wizard);
    } catch (error) {
        setWizardStep(wizard, 1);
        showFormMessage(form, error.message || 'Could not start checkout. Please try again.', true);
    } finally {
        setButtonLoading(continueBtn, false);
    }
}

function continueToIntroCall(wizard) {
    stripeSession.abandonReported = true;
    if (typeof window.showReserveIntroCall !== 'function') {
        setConfirmState(wizard, true);
        return;
    }
    closeMembershipJoinModals();
    window.showReserveIntroCall();
}

async function handleWizardSubmit(wizard, form) {
    hideFormMessage(form);
    const billed = wizard.querySelector('[data-membership-join-billed-name]');
    if (billed?.value?.trim() && wizard.querySelector('[data-membership-join-name]')) {
        wizard.querySelector('[data-membership-join-name]').value = billed.value.trim();
    }
    const billedEmail = wizard.querySelector('[data-membership-join-billed-email]');
    const stepEmail = wizard.querySelector('[data-membership-join-email]');
    if (billedEmail?.value?.trim() && stepEmail) {
        stepEmail.value = billedEmail.value.trim();
    }

    if (!validateCheckoutFields(wizard)) {
        return;
    }

    const identity = wizardIdentity(wizard);
    const submitBtn = form.querySelector('[data-membership-join-submit]');
    setButtonLoading(submitBtn, true);
    try {
        if (identity.plan !== 'core-plus' && identity.plan !== 'core') {
            throw new Error('Unable to submit this plan.');
        }

        const sessionKey = () => [
            stripeSession.clientSecret,
            stripeSession.email,
            stripeSession.name,
            stripeSession.plan,
            stripeSession.billing,
            stripeSession.promoCode || '',
            stripeSession.channelHint || '',
        ].join('|');
        const sessionBefore = sessionKey();
        await prepareCheckout(wizard);
        if (!stripeSession.paymentElement || !stripeSession.stripe || !stripeSession.elements) {
            throw new Error('Card payments are unavailable right now.');
        }
        if (sessionKey() !== sessionBefore) {
            return;
        }

        const intentId = paymentIntentIdFromClientSecret(stripeSession.clientSecret);
        try {
            await confirmMembershipPayment({
                stripe: stripeSession.stripe,
                elements: stripeSession.elements,
                clientSecret: stripeSession.clientSecret,
                name: billed?.value?.trim() || identity.name,
                email: identity.email,
                country: identity.country,
                postalCode: identity.postalCode,
                tier: identity.plan === 'core' ? 'core' : 'coreplus',
                billing: identity.billing,
            });
            reportMembershipPaymentStatus(intentId, { kind: 'paid' });
            clearMembershipCampaign();
            if (wizard.dataset.stepCount === '3') {
                continueToIntroCall(wizard);
            } else {
                setConfirmState(wizard, true);
            }
        } catch (error) {
            if (error?.reportToSlack) {
                reportMembershipPaymentStatus(intentId, {
                    kind: 'confirm_failed',
                    message: error.reportToSlack.message,
                    decline_code: error.reportToSlack.decline_code,
                });
            }
            throw error;
        }
    } catch (error) {
        showFormMessage(form, error.message || 'Something went wrong. Please try again.', true);
    } finally {
        setButtonLoading(submitBtn, false);
    }
}

function resetOpenSuccess(modal) {
    const panel = modal.querySelector('[data-membership-join-form-panel]');
    const success = modal.querySelector('[data-membership-join-success]');
    modal.classList.remove('is-success');
    if (panel) {
        panel.hidden = false;
    }
    if (success) {
        success.hidden = true;
    }
}

export function showMembershipOpenSuccess(modal) {
    const node = modal || document.querySelector('[data-membership-join-modal="open"]');
    if (!node) {
        return;
    }
    const panel = node.querySelector('[data-membership-join-form-panel]');
    const success = node.querySelector('[data-membership-join-success]');
    node.classList.add('is-success');
    if (panel) {
        panel.hidden = true;
    }
    if (success) {
        success.hidden = false;
    }
}

function setPayButtonLabel(wizard, direct) {
    const text = wizard.querySelector('[data-membership-join-submit] .submit-text');
    if (!text) {
        return;
    }
    if (!text.dataset.defaultLabel) {
        text.dataset.defaultLabel = text.textContent;
    }
    text.textContent = direct ? 'Pay for my seat' : text.dataset.defaultLabel;
}

function setPaymentDirect(wizard, direct) {
    wizard.classList.toggle('is-payment-direct', direct);
    const emailField = wizard.querySelector('[data-membership-join-billed-email-field]');
    const emailInput = wizard.querySelector('[data-membership-join-billed-email]');
    if (emailField) {
        emailField.toggleAttribute('hidden', !direct);
    }
    if (emailInput) {
        emailInput.required = Boolean(direct);
    }
    const countryField = wizard.querySelector('[data-membership-join-billed-country-field]');
    const countryInput = wizard.querySelector('#membership-join-country');
    if (countryField) {
        countryField.toggleAttribute('hidden', !direct);
    }
    if (countryInput) {
        countryInput.required = Boolean(direct);
    }
    setPayButtonLabel(wizard, direct);
}

function identityReady(identity) {
    return identity.name !== ''
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity.email)
        && /^[A-Z]{2}$/.test(String(identity.country || '').trim().toUpperCase());
}

let directCheckoutTimer = 0;

function scheduleDirectCheckout(wizard) {
    if (!wizard.classList.contains('is-payment-direct') || wizard.classList.contains('is-confirm')) {
        return;
    }
    window.clearTimeout(directCheckoutTimer);
    directCheckoutTimer = window.setTimeout(async () => {
        const identity = wizardIdentity(wizard);
        if (!identityReady(identity)) {
            return;
        }
        const form = wizard.querySelector('#membership-join-wizard-form');
        hideFormMessage(form);
        try {
            await prepareCheckout(wizard);
        } catch (error) {
            showFormMessage(form, error.message || 'Could not start checkout. Please try again.', true);
        }
    }, 400);
}

function openPrefilledCheckout(wizard, plan, options) {
    const form = wizard.querySelector('#membership-join-wizard-form');
    form?.reset();
    hideFormMessage(form);
    wizard.dataset.billing = options.billing === 'yearly' ? 'yearly' : 'monthly';
    setPaymentDirect(wizard, false);
    applyPlan(wizard, plan);

    const name = String(options.name || '').trim();
    const email = String(options.email || '').trim();
    const nameInput = wizard.querySelector('[data-membership-join-name]');
    const emailInput = wizard.querySelector('[data-membership-join-email]');
    const billed = wizard.querySelector('[data-membership-join-billed-name]');
    const newsletter = wizard.querySelector('[data-membership-join-newsletter]');
    if (nameInput) {
        nameInput.value = name;
    }
    if (billed) {
        billed.value = name;
    }
    if (emailInput) {
        emailInput.value = email;
    }
    if (newsletter) {
        newsletter.checked = Boolean(options.newsletterOptIn);
    }
    const countrySelect = wizard.querySelector('#membership-join-country');
    const country = String(options.country || '').trim().toUpperCase();
    if (countrySelect && country) {
        countrySelect.value = country;
    }

    setConfirmState(wizard, false);
    setWizardStep(wizard, 2, 3);
    setOpenState(wizard, true);
    // Country comes from step 1 of the reserve modal; Stripe needs it before the card form loads.
    if (!identityReady(wizardIdentity(wizard))) {
        return;
    }
    prepareCheckout(wizard).catch((error) => {
        showFormMessage(form, error.message || 'Could not start checkout. Please try again.', true);
    });
}

export function openMembershipJoinModal(plan, options = {}) {
    const key = plan === 'core-plus' || plan === 'core' || plan === 'open' ? plan : 'open';
    const onPaymentPage = document.body.classList.contains('membership-payment');
    const prefilledCheckout = Boolean(options.prefilled) && (key === 'core' || key === 'core-plus');
    const direct = !prefilledCheckout && (options.step === 2 || (onPaymentPage && options.step !== 1));
    const free = document.querySelector('[data-membership-join-modal="open"]');
    const wizard = document.querySelector('[data-membership-join-modal="wizard"]');

    document.querySelectorAll('.membership-join.is-open').forEach((modal) => {
        setOpenState(modal, false);
    });

    if (key === 'open') {
        if (free) {
            resetOpenSuccess(free);
            free.querySelector('form')?.reset();
            setOpenState(free, true);
            free.querySelector('#membership-join-free-name')?.focus();
        }
        return;
    }

    if (wizard && prefilledCheckout) {
        openPrefilledCheckout(wizard, key, options);
        return;
    }

    if (wizard) {
        wizard.dataset.billing = options.billing === 'yearly' ? 'yearly' : 'monthly';
        setPaymentDirect(wizard, direct);
        applyPlan(wizard, key);
        setConfirmState(wizard, false);
        setWizardStep(wizard, direct ? 2 : 1, 2);
        setOpenState(wizard, true);
        if (direct) {
            const emailField = wizard.querySelector('[data-membership-join-billed-email]');
            if (emailField && !emailField.value) {
                emailField.focus();
            }
            scheduleDirectCheckout(wizard);
        }
    }
}

export function closeMembershipJoinModals() {
    const wizard = document.querySelector('[data-membership-join-modal="wizard"]');
    reportCheckoutAbandoned(wizard);
    document.querySelectorAll('.membership-join').forEach((modal) => {
        setOpenState(modal, false);
        if (modal.matches('[data-membership-join-modal="wizard"]')) {
            resetWizard(modal);
        } else {
            resetOpenSuccess(modal);
            modal.querySelector('form')?.reset();
        }
    });
    invalidateCheckout();
}

export function initMembershipJoinModal() {
    const free = document.querySelector('[data-membership-join-modal="open"]');
    const wizard = document.querySelector('[data-membership-join-modal="wizard"]');
    if (!free && !wizard) {
        return;
    }

    if (wizard) {
        wizard.querySelector('[data-membership-join-continue]')?.addEventListener('click', () => {
            goToCheckout(wizard);
        });

        wizard.addEventListener('click', (event) => {
            if (!event.target?.closest?.('[data-membership-join-close]')) {
                return;
            }
            reportCheckoutAbandoned(wizard);
        }, true);

        wizard.querySelectorAll('[data-membership-join-quote]').forEach((dot) => {
            dot.addEventListener('click', () => {
                setQuote(wizard, Number(dot.getAttribute('data-membership-join-quote')));
            });
        });

        const billed = wizard.querySelector('[data-membership-join-billed-name]');
        const name = wizard.querySelector('[data-membership-join-name]');
        billed?.addEventListener('input', () => {
            if (name) {
                name.value = billed.value;
            }
            scheduleDirectCheckout(wizard);
        });

        const billedEmail = wizard.querySelector('[data-membership-join-billed-email]');
        const stepEmail = wizard.querySelector('[data-membership-join-email]');
        billedEmail?.addEventListener('input', () => {
            if (stepEmail) {
                stepEmail.value = billedEmail.value;
            }
            scheduleDirectCheckout(wizard);
        });

        wizard.querySelector('[data-membership-join-promo-toggle]')?.addEventListener('click', () => {
            openPromoPanel(wizard);
        });
        wizard.querySelector('[data-membership-join-promo-apply]')?.addEventListener('click', () => {
            applyPromoFromInput(wizard);
        });
        wizard.querySelector('[data-membership-join-promo-remove]')?.addEventListener('click', () => {
            removePromo(wizard);
        });
        wizard.querySelector('[data-membership-join-promo-input]')?.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter') {
                return;
            }
            event.preventDefault();
            applyPromoFromInput(wizard);
        });

        wizard.querySelector('#membership-join-country')?.addEventListener('change', () => {
            if (wizard.classList.contains('is-confirm')) {
                return;
            }
            if (wizard.classList.contains('is-payment-direct')) {
                scheduleDirectCheckout(wizard);
                return;
            }
            if (!wizard.classList.contains('is-checkout') || !identityReady(wizardIdentity(wizard))) {
                return;
            }
            const form = wizard.querySelector('#membership-join-wizard-form');
            hideFormMessage(form);
            prepareCheckout(wizard).catch((error) => {
                showFormMessage(form, error.message || 'Could not start checkout. Please try again.', true);
            });
        });

        wizard.querySelector('#membership-join-wizard-form')?.addEventListener('submit', (event) => {
            event.preventDefault();
            event.stopPropagation();
            handleWizardSubmit(wizard, event.currentTarget);
        });
    }

    const returnParams = new URLSearchParams(window.location.search);
    const returnStatus = getStripeReturnStatus();
    const returnIntent = returnParams.get('payment_intent') || '';
    const returnPlan = window.location.hash.replace('#', '') === 'join-core' ? 'core' : 'core-plus';
    const returnBilling = returnParams.get('membership_billing') === 'yearly' ? 'yearly' : 'monthly';
    const paymentPage = document.body.classList.contains('membership-payment');
    if (returnStatus === 'succeeded' && returnIntent) {
        reportMembershipPaymentStatus(returnIntent, { kind: 'paid' });
        clearMembershipCampaign();
    }
    if (returnStatus === 'succeeded' && !paymentPage) {
        window.peMpPendingIntroCall = true;
        if (typeof window.showReserveIntroCall === 'function') {
            window.showReserveIntroCall();
            window.peMpPendingIntroCall = false;
        }
        clearStripeReturnParams();
    } else if (returnStatus === 'succeeded' && wizard) {
        applyPlan(wizard, returnPlan);
        setOpenState(wizard, true);
        setConfirmState(wizard, true);
        clearStripeReturnParams();
    } else if (returnStatus === 'failed' && wizard) {
        reportMembershipPaymentStatus(returnIntent, { kind: 'confirm_failed' });
        wizard.dataset.billing = returnBilling;
        setPaymentDirect(wizard, paymentPage);
        applyPlan(wizard, returnPlan);
        setWizardStep(wizard, paymentPage ? 2 : 1, 2);
        setOpenState(wizard, true);
        showFormMessage(
            wizard.querySelector('#membership-join-wizard-form'),
            'Payment failed. Please try another method.',
            true
        );
        clearStripeReturnParams();
    }
}
